/* eslint-disable @typescript-eslint/no-explicit-any */
/* src/components/layout/Logo.tsx */
'use client';

import React, { useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';

import { useAppSelector } from '@/hooks/useRedux';
import { imageSrc } from '@/utils/asset';
import { getRuntimeOrg } from '@/utils/runtimeOrg';
import { bometConfig } from '@/config/bomet.config';
import { nyandaruaConfig } from '@/config/nyandarua.config';
import { baringoConfig } from '@/config/baringo.config';
import { limsConfig } from '@/config/lims.config';

const ORG_CONFIG_MAP: Record<string, any> = {
  bomet: bometConfig,
  nyandarua: nyandaruaConfig,
  baringo: baringoConfig,
  lims: limsConfig,
};

type LogoProps = {
  href?: string;
  width?: number;
  height?: number;
  className?: string; // wrapper
  imgClassName?: string; // image element
  priority?: boolean;
};

export default function Logo({
  href = '/',
  width = 256,
  height = 75,
  className = '',
  imgClassName = '',
  priority = true,
}: LogoProps) {
  /**
   * Pull org config internally
   * Adjust selector if your cfg lives elsewhere
   */
  const reduxCfg = useAppSelector((s: any) => s?.config?.org);

  const cfg = useMemo(() => {
    if (reduxCfg?.logo) return reduxCfg;
    const htmlOrg =
      typeof document !== 'undefined'
        ? document.documentElement.dataset.org
        : undefined;
    const org = (htmlOrg || getRuntimeOrg() || 'bomet').toLowerCase();
    return ORG_CONFIG_MAP[org] ?? reduxCfg ?? {};
  }, [reduxCfg]);

  const alt = cfg?.name || 'Home';

  const src = useMemo(() => {
    return imageSrc(cfg?.logo || '/logo.svg');
  }, [cfg?.logo]);

  return (
    <div
      className={`
        flex items-center
        w-full flex-shrink-0
        ${className}
      `}
    >
      <Link href={href} aria-label={alt} className="block w-full">
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          priority={priority}
          className={`w-full h-auto object-contain ${imgClassName}`}
        />
      </Link>
    </div>
  );
}
